import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { buildPeriodMarks, buildVisitSteps, termCode } from '../utils/ssPeriodLine';
import { daysUntilDue, formatDueDateLocal, parseLocalDate } from '../utils/dueDate';
import { anniversaryForInspection } from '../utils/inspectionDueDate';
import { getJobRowStyle, mailListLabel } from '../utils/jobStatusColor';
import { getAdminNextAction } from '../constants/jobModel';

function resultClass(result, size) {
  const sizeClass = size === 'small' ? 'text-xs' : 'text-base font-semibold';
  if (result === 'pass') return `${sizeClass} text-green-700`;
  if (result === 'fail') return `${sizeClass} text-red-700`;
  return `${sizeClass} text-gray-900`;
}

function toneClass(tone) {
  if (tone === 'wait') return 'bg-blue-600 text-white border-blue-700';
  if (tone === 'set') return 'bg-green-600 text-white border-green-700';
  if (tone === 'fail') return 'bg-red-600 text-white border-red-700';
  return 'bg-green-100 text-gray-600 border-green-300';
}

function flowLabel(current) {
  if (current === 'completed') return 'Completed';
  if (current === 'progress') return 'In Progress';
  if (current === 'scheduled') return 'Scheduled';
  return 'Scheduling';
}

const MAIL_KIND = {
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  read: 'Read',
};

function ScheduleMailBox({ jobId, mailKind = 'propose' }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const path = mailKind === 'confirm' ? 'visit-confirm-mail' : 'schedule-mail';
  const { data } = useQuery({
    queryKey: ['job', String(jobId), path],
    queryFn: async () => (await api.get(`/jobs/${jobId}/${path}`)).data,
    enabled: !!jobId,
  });

  const hasSent = !!data?.hasSent;
  const log = Array.isArray(data?.log) ? data.log : [];

  const sendMail = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/jobs/${jobId}/${path}`, {
          to: to.trim(),
          subject: subject.trim(),
          body,
        })
      ).data,
    onSuccess: (payload) => {
      const failed = (payload?.log || []).some((row) => row.kind === 'failed' && !payload?.hasSent);
      if (payload?.hasSent) toast.success('Schedule mail sent');
      else toast.error(failed ? 'Mail was not delivered' : 'Mail was not delivered');
      setOpen(false);
      queryClient.invalidateQueries(['job', String(jobId), path]);
      queryClient.invalidateQueries(['job', String(jobId)]);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not send the mail'),
  });

  function openForm() {
    setTo(data?.draft?.to || '');
    setSubject(data?.draft?.subject || '');
    setBody(data?.draft?.body || '');
    setOpen(true);
  }

  return (
    <>
      {open ? (
        <form
          className="mt-3 w-full basis-full space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            sendMail.mutate();
          }}
        >
          <label className="block text-xs text-gray-600">
            To
            <input
              type="email"
              required
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-green-300 rounded-lg bg-green-100 text-sm"
            />
          </label>
          <label className="block text-xs text-gray-600">
            Subject
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-green-300 rounded-lg bg-green-100 text-sm"
            />
          </label>
          <label className="block text-xs text-gray-600">
            Message
            <textarea
              required
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-green-300 rounded-lg bg-green-100 text-sm"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-3 py-1 rounded-lg border border-green-300 bg-green-100 text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sendMail.isPending}
              className="px-3 py-1 rounded-lg bg-primary-600 text-white text-xs disabled:opacity-50"
            >
              {sendMail.isPending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={openForm}
          className="px-3 py-1 rounded-lg bg-primary-600 text-white text-xs"
        >
          {hasSent ? 're-send mail' : mailKind === 'confirm' ? 'Send mail to confirm the visit' : 'Send mail to make Schedule'}
        </button>
      )}
      {log.length ? (
        <ul className="mt-3 w-full basis-full text-xs text-gray-800 space-y-1">
          {log.map((row, i) => (
            <li key={`${row.id}-${row.kind}-${i}`}>
              <span className="font-semibold">{MAIL_KIND[row.kind] || row.kind}</span>
              {row.at ? ` · ${row.at}` : ''}
              {row.detail ? ` · ${row.detail}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function externalResultLabel(job) {
  const v = String(job?.external_result || job?.inspection_outcome || '')
    .toLowerCase()
    .replace(/_/g, '-');
  if (v === 'yes') return 'yes';
  if (v === 'not-yet' || v === 'notyet') return 'not yet';
  return '';
}

function InspectionOutcomeBox({ job }) {
  const queryClient = useQueryClient();
  const recorded = externalResultLabel(job);
  const [reason, setReason] = useState(job?.external_result_reason || '');
  const [showNotYet, setShowNotYet] = useState(recorded === 'not yet');
  const [file, setFile] = useState(null);

  const save = useMutation({
    mutationFn: async ({ answer, reasonText, uploadFile }) => {
      const form = new FormData();
      form.append('answer', answer);
      if (reasonText) form.append('reason', reasonText);
      if (uploadFile) form.append('file', uploadFile);
      return (await api.post(`/jobs/${job.id}/external-iqp-result`, form)).data?.data;
    },
    onSuccess: () => {
      toast.success('External IQP result saved');
      setFile(null);
      queryClient.invalidateQueries(['job', String(job.id)]);
      queryClient.invalidateQueries(['building']);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not save'),
  });

  const btnClass = (key) =>
    `px-3 py-1 rounded-lg text-xs disabled:opacity-50 ${
      recorded === key
        ? 'bg-primary-600 text-white border border-primary-600'
        : 'border border-green-300 bg-green-100 text-gray-900'
    }`;

  return (
    <div className="w-full basis-full space-y-2">
      <p className="text-sm font-medium text-gray-900">Did you get result from external IQP?</p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => {
            setShowNotYet(false);
            save.mutate({ answer: 'yes' });
          }}
          className={btnClass('yes')}
        >
          Yes
        </button>
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => setShowNotYet(true)}
          className={btnClass('not yet')}
        >
          Not Yet
        </button>
      </div>

      {recorded === 'yes' ? (
        <div className="rounded-lg border border-green-300 bg-white p-3 space-y-2">
          <p className="text-xs text-gray-700">Upload the certificate / report (same next step as worker Done).</p>
          <input
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs"
          />
          <button
            type="button"
            disabled={save.isPending || !file}
            onClick={() => save.mutate({ answer: 'yes', uploadFile: file })}
            className="px-3 py-1 rounded-lg bg-primary-600 text-white text-xs disabled:opacity-50"
          >
            Upload and continue
          </button>
        </div>
      ) : null}

      {showNotYet ? (
        <div className="rounded-lg border border-green-300 bg-white p-3 space-y-2">
          <label className="block text-xs text-gray-700">
            Reason
            <textarea
              className="mt-1 w-full border border-green-300 rounded-lg px-2 py-1.5 text-sm min-h-[4rem]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Waiting on revisit / parts / reply…"
            />
          </label>
          <button
            type="button"
            disabled={save.isPending || !reason.trim()}
            onClick={() => save.mutate({ answer: 'not_yet', reasonText: reason.trim() })}
            className="px-3 py-1 rounded-lg border border-green-300 bg-green-100 text-xs disabled:opacity-50"
          >
            Save Not Yet
          </button>
        </div>
      ) : null}

      {recorded ? (
        <p className="text-xs text-gray-800">
          Recorded: {recorded}
          {recorded === 'not yet' && job?.external_result_reason
            ? ` — ${job.external_result_reason}`
            : ''}
          {recorded === 'yes'
            ? ' — upload the file to continue like Done'
            : ' — admin can follow up from here'}
        </p>
      ) : (
        <p className="text-xs text-gray-600">
          Admin can choose Yes or Not Yet here even if the day-after email was not used. The email
          path writes the same Yes / Not Yet state.
        </p>
      )}
    </div>
  );
}

function reportForPeriod(card, reports) {
  return (reports || []).find((report) => {
    if (card.visit?.id && report.job_id && String(report.job_id) === String(card.visit.id)) return true;
    const issued = parseLocalDate(report.issued_at);
    const start = parseLocalDate(card.start);
    const due = parseLocalDate(card.due);
    if (!issued || !due) return false;
    if (start && issued < start) return false;
    return issued <= due;
  });
}

export default function SsPeriodLine({ job, certificateLabel, onOpenCertificate, reports, onOpenReport }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [focus, setFocus] = useState(null);
  const [dateDraft, setDateDraft] = useState('');

  const frequency = job?.ss_frequency || job?.inspection_frequency || 'ANNUAL';
  const anchor =
    anniversaryForInspection(job);
  const visits = Array.isArray(job?.ss_visits) && job.ss_visits.length ? job.ss_visits : [job];
  const periods = buildPeriodMarks({ frequency, anchor, visits });
  const steps = buildVisitSteps({
    job,
    notifyFacts: job?.notify_facts,
    periodDue: periods.currentDue,
    missed: periods.missed,
  });
  const live = ['scheduling', 'scheduled', 'progress'].includes(steps.current) ? steps.current : null;
  const shown = focus || live;
  const term = termCode(periods.meta.tag, periods.current);
  const closed = ['COMPLETED', 'FAILED', 'REJECTED'].includes(String(job?.status || '').toUpperCase());
  const notice = steps.scheduling.when
    ? `${job?.external_mail ? 'Mail' : 'Notice'} ${steps.scheduling.when}${
        steps.scheduling.status ? ` · ${steps.scheduling.status}` : ''
      }`
    : job?.external_mail
      ? 'Mail not sent'
      : 'Notice not sent';
  const dueText = formatDueDateLocal(periods.currentDue);
  const statusStyle = getJobRowStyle(job?.status, daysUntilDue(periods.currentDue));
  const workerName =
    job?.assigned_worker_name || job?.assignedWorker?.name || job?.assigned_worker_id || 'Not assigned';

  const saveDate = useMutation({
    mutationFn: async (scheduled_date) => {
      const res = await api.patch(`/jobs/${job.id}`, { scheduled_date, status: 'SCHEDULED' });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Scheduled date saved');
      setDateDraft('');
      queryClient.invalidateQueries(['job', String(job.id)]);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not save the date'),
  });

  const resetOffer = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${job.id}/reschedule-offer`)).data,
    onSuccess: (data) => {
      toast.success(data?.message || 'New time sent');
      queryClient.invalidateQueries(['job', String(job.id)]);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not send a new time'),
  });

  const shownDate = dateDraft || steps.scheduled || '';

  function chip(key, tone, text) {
    const active = shown === key;
    return (
      <button
        type="button"
        onClick={() => setFocus(key)}
        className={`px-2 py-0.5 rounded-full border text-xs ${toneClass(tone)} ${
          active ? 'ring-2 ring-offset-1 ring-gray-700' : ''
        }`}
      >
        {text}
      </button>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow p-4 mb-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-gray-800">{periods.meta.label}</span>
        <span className="inline-flex items-baseline gap-1">
          {periods.marks.map((mark, i) =>
            mark.paren ? (
              <span key={`p-${i}`} className="inline-flex items-baseline gap-1">
                <span className="text-gray-500">(</span>
                {mark.paren.map((item) => (
                  <span key={item.label} className={resultClass(item.result, 'large')}>
                    {item.label}
                  </span>
                ))}
                <span className="text-gray-500">)</span>
              </span>
            ) : (
              <span key={mark.label} className={resultClass(mark.result, mark.size)}>
                {mark.label}
              </span>
            )
          )}
        </span>
        {chip(
          'scheduling',
          steps.scheduling.tone,
          `Scheduling${steps.scheduling.when ? ` ${steps.scheduling.when}` : ''}${
            steps.scheduling.status ? ` · ${steps.scheduling.status}` : ''
          }`
        )}
        {chip(
          'scheduled',
          steps.scheduledStep.tone,
          `Scheduled${steps.scheduledStep.when ? ` ${steps.scheduledStep.when}` : ''}${
            steps.scheduledStep.status ? ` · ${steps.scheduledStep.status}` : ''
          }`
        )}
        {chip(
          'progress',
          (() => {
            const outcome = externalResultLabel(job);
            if (outcome === 'yes') return 'set';
            if (outcome === 'not yet') return 'wait';
            return steps.progress.tone;
          })(),
          (() => {
            const outcome = externalResultLabel(job);
            return outcome ? `Result · ${outcome}` : 'Result';
          })()
        )}
        <span className={`px-2 py-0.5 rounded-full border text-xs ${toneClass(steps.completedTone)}`}>
          Completed
        </span>
        {steps.health === 'overdue' ? (
          <span className="px-2 py-0.5 rounded-full border text-xs bg-red-700 text-white border-red-800">
            Over Due
          </span>
        ) : steps.health === 'urgent' ? (
          <span className="px-2 py-0.5 rounded-full border text-xs bg-amber-500 text-white border-amber-600">
            Urgent
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full border text-xs bg-blue-600 text-white border-blue-700">
            Normal
          </span>
        )}
        {steps.changeRequested ? (
          <button
            type="button"
            disabled={resetOffer.isPending}
            onClick={() => resetOffer.mutate()}
            className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs disabled:opacity-50"
          >
            Reset
          </button>
        ) : null}
      </div>

      {shown === 'scheduling' ? (
        <div className="rounded-lg border border-green-300 bg-green-100 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">Scheduling</p>
            <ScheduleMailBox jobId={job.id} />
          </div>
          <p className="text-gray-700 mt-1">
            {notice}
            {job?.schedule_change_note ? ` · ${job.schedule_change_note}` : ''}
          </p>
          {steps.changeRequested ? null : (
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!shownDate) return;
                saveDate.mutate(shownDate);
              }}
            >
              <label className="text-xs text-gray-600" htmlFor={`sched-${job.id}`}>
                Scheduled date
              </label>
              <input
                id={`sched-${job.id}`}
                type="date"
                value={shownDate}
                onChange={(e) => setDateDraft(e.target.value)}
                className="px-2 py-1 border border-green-300 rounded-lg bg-green-100 text-sm"
              />
              <button
                type="submit"
                disabled={saveDate.isPending || !shownDate}
                className="px-3 py-1 rounded-lg bg-primary-600 text-white text-xs disabled:opacity-50"
              >
                Save date
              </button>
            </form>
          )}
        </div>
      ) : null}

      {shown === 'scheduled' ? (
        <div className="rounded-lg border border-green-300 bg-green-100 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">Scheduled</p>
            <ScheduleMailBox jobId={job.id} mailKind="confirm" />
          </div>
          <p className="text-gray-700 mt-1">
            {steps.scheduledStep.when || 'No date back yet'}
            {steps.scheduledStep.status ? ` · ${steps.scheduledStep.status}` : ''}
          </p>
        </div>
      ) : null}

      {shown === 'progress' ? (
        <div className="rounded-lg border border-green-300 bg-green-100 p-3 text-sm">
          <InspectionOutcomeBox job={job} />
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-[980px] w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-2 pr-3 font-medium">Due Date</th>
              <th className="py-2 pr-3 font-medium">SS code</th>
              <th className="py-2 pr-3 font-medium">Assigned Worker</th>
              <th className="py-2 pr-3 font-medium">Workflow</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">Issued certificate</th>
              <th className="py-2 pr-3 font-medium">Admin next</th>
            </tr>
          </thead>
          <tbody>
            <tr className="align-top">
              <td className="py-2 pr-3 whitespace-nowrap">
                {dueText} ({term})
              </td>
              <td className="py-2 pr-3">{job?.ss_code || '—'}</td>
              <td className="py-2 pr-3">
                <div>{workerName}</div>
                <div className="text-xs text-gray-600 mt-1">{notice}</div>
              </td>
              <td className="py-2 pr-3">{flowLabel(steps.current)}</td>
              <td className="py-2 pr-3">
                <div className="flex flex-col gap-1">
                  {!closed && steps.health === 'overdue' ? (
                    <span className="inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-medium bg-red-800 text-white">
                      Over Due
                    </span>
                  ) : null}
                  <span
                    className={`inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-medium ${
                      steps.current !== 'progress' &&
                      ['IN_PROGRESS', 'REPAIRING'].includes(String(job?.status || '').toUpperCase())
                        ? 'bg-blue-100 text-blue-900'
                        : statusStyle.badge
                    }`}
                  >
                    {steps.current !== 'progress' &&
                    ['IN_PROGRESS', 'REPAIRING'].includes(String(job?.status || '').toUpperCase())
                      ? flowLabel(steps.current)
                      : mailListLabel(job)}
                  </span>
                </div>
              </td>
              <td className="py-2 pr-3">
                {certificateLabel ? (
                  <button type="button" onClick={onOpenCertificate} className="underline text-left">
                    {certificateLabel}
                  </button>
                ) : (
                  '—'
                )}
              </td>
              <td className="py-2 pr-3 text-xs text-gray-600 max-w-[220px]">
                {closed ? 'Completed' : getAdminNextAction(job?.status)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {periods.cards.map((card) => {
          const start = parseLocalDate(card.start);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const notStarted = !!(start && today < start);
          const report = reportForPeriod(card, reports);
          const past = daysUntilDue(card.due) != null && daysUntilDue(card.due) < 0;
          return (
            <div
              key={card.code}
              aria-disabled={notStarted}
              className={
                notStarted
                  ? 'rounded-lg border border-gray-200 bg-gray-100 p-3 text-gray-400 pointer-events-none'
                  : 'rounded-lg border border-green-300 bg-green-100 p-3'
              }
            >
              <p className={notStarted ? 'text-base font-semibold text-gray-400' : resultClass(card.result, 'large')}>
                {card.label}
              </p>
              <p className={`text-xs mt-1 ${notStarted ? 'text-gray-400' : 'text-gray-600'}`}>
                due {formatDueDateLocal(card.due)}
              </p>
              <p className={`text-xs ${notStarted ? 'text-gray-400' : 'text-gray-600'}`}>
                {card.visit?.status || 'Not started'}
                {card.visit?.scheduled_date ? ` · ${String(card.visit.scheduled_date).slice(0, 10)}` : ''}
              </p>
              <p className="text-xs mt-1">
                {card.result === 'pass' ? (
                  <span className={notStarted ? 'text-gray-400' : 'text-green-700'}>Pass</span>
                ) : card.result === 'fail' ? (
                  <span className={notStarted ? 'text-gray-400' : 'text-red-700'}>Fail</span>
                ) : (
                  <span className="text-gray-500">Open</span>
                )}
              </p>
              {report && !notStarted ? (
                <button
                  type="button"
                  onClick={() => onOpenReport?.(report)}
                  className="inline-block mt-2 text-xs font-medium text-green-700 underline"
                >
                  Report
                </button>
              ) : !past ? (
                <p className="mt-2 text-xs text-gray-400">Not yet</p>
              ) : (
                <button
                  type="button"
                  onClick={() => window.alert('No report.')}
                  className="inline-block mt-2 text-xs font-medium text-red-700 underline"
                >
                  Report
                </button>
              )}
              {card.visit?.id && String(card.visit.id) !== String(job.id) && !notStarted ? (
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/${card.visit.id}?tab=building`)}
                  className="block mt-1 text-xs text-gray-600 underline"
                >
                  Open this period
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
